import { Link } from "react-router-dom";
import { ArrowRight, MessageCircle } from "lucide-react";
import { waLink, WA_MESSAGES } from "@/lib/whatsapp";

const BADGE_THEME = {
  Free: "bg-forest text-white",
  "Most Popular": "bg-clay text-white",
  Recommended: "bg-clay-dark text-white",
};

function cardThemes(featured) {
  return {
    card: featured
      ? "bg-forest text-white border-forest/10"
      : "bg-white border-sand-300 shadow-[0_1px_2px_rgba(0,0,0,0.02)]",
    duration: featured ? "bg-clay text-white" : "bg-sand-200 text-forest",
    durationMeta: featured ? "text-white/70" : "text-ink-muted",
    title: featured ? "" : "text-forest",
    tagline: featured ? "text-clay-light" : "text-clay",
    benefit: featured ? "text-white/85" : "text-ink-soft",
    cta: featured ? "btn-accent" : "btn-primary",
    detailsLink: featured
      ? "text-white/90 hover:text-white"
      : "text-forest hover:text-clay",
  };
}

function ProgramCard({ p, featured }) {
  const msg = WA_MESSAGES[p.cta_message_key] || WA_MESSAGES.program(p.title);
  const t = cardThemes(featured);
  const badge = p.badge;

  return (
    <article
      data-testid={`program-card-${p.slug}`}
      className={`group relative rounded-3xl border p-7 md:p-8 flex flex-col transition-all duration-300 hover:-translate-y-1 hover:shadow-xl ${t.card}`}
    >
      {badge && (
        <span
          className={`absolute -top-3 left-7 rounded-full px-3 py-1 text-[10px] tracking-[0.18em] uppercase font-medium ${BADGE_THEME[badge] || "bg-forest text-white"}`}
        >
          {badge}
        </span>
      )}

      <div className="flex items-center gap-2 text-xs">
        <span className={`px-2.5 py-1 rounded-full ${t.duration}`}>{p.duration}</span>
        <span className={t.durationMeta}>· {p.level}</span>
      </div>

      <h3 className={`mt-4 font-serif text-2xl md:text-3xl leading-tight ${t.title}`}>
        {p.title}
      </h3>
      <p className={`mt-2 text-sm font-medium leading-snug italic ${t.tagline}`}>
        {p.tagline}
      </p>
      <p className={`mt-3 text-sm leading-relaxed ${t.benefit}`}>
        {p.benefit}
      </p>

      <div className="mt-auto pt-7 flex flex-wrap gap-3">
        <a
          href={waLink(msg)}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`program-cta-${p.slug}`}
          className={t.cta}
        >
          <MessageCircle className="h-4 w-4" /> Book Your Free Consultation
        </a>
        <Link
          to={`/programs/${p.slug}`}
          data-testid={`program-details-${p.slug}`}
          className={`inline-flex items-center gap-1.5 text-sm font-medium self-center ${t.detailsLink}`}
        >
          Full details &amp; pricing <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </article>
  );
}

export default function ProgramsSection({ programs }) {
  return (
    <section id="programs" className="section-pad">
      <div className="container-x">
        <div className="max-w-2xl">
          <p className="eyebrow">Programs</p>
          <h2 className="mt-3 font-serif text-3xl sm:text-4xl md:text-5xl text-forest leading-tight">
            Choose the Gurukul path that fits your life.
          </h2>
          <p className="mt-4 text-ink-soft">
            Three structured 1-on-1 programs — start with a complimentary 7-day initiation, commit to 90 days of inner alchemy, or transform end-to-end over a full year.
          </p>
        </div>

        <div className="mt-12 md:mt-14 grid gap-6 lg:grid-cols-3 items-stretch">
          {programs.map((p) => (
            <ProgramCard key={p.slug} p={p} featured={p.slug === "inner-alchemy-90-day"} />
          ))}
        </div>

        <p className="mt-6 text-xs text-ink-muted text-center">
          Results depend on consistency, nutrition adherence, and individual factors. Programs include a 7-day money-back guarantee.
        </p>
      </div>
    </section>
  );
}
